import {
	MetricKeysDataPoint,
	Period,
} from '../../lib/enums/bi-enums'
import { padMonthsWithZero } from '../../lib/utils/bi-utils/metricsDataCalculator'
import {
	MetricFrontendDataPoint,
	PerformanceMetricsApiData,
} from '../service/models/performanceMetrics'

export interface UtcDateValues {
	currentMonth: number
	currentYear: number
	startMonth: number
	startYear: number
	startFullYear: number[] | undefined
	compareCurrYear: number
	comparePriorYear: number
}

/**
 * Checks if all elements in an array are either 0 or undefined.
 * Handles null, undefined, or non-array inputs by returning true, similar to an empty array.
 *
 * @param arr - The array of numbers or undefined values to check. Can be null or undefined.
 * @returns `true` if all elements are 0 or undefined, or if `arr` is null, undefined, not an array, or an empty array. Returns `false` otherwise.
 */
export const isAllZeroOrUndefined = (
	arr: ReadonlyArray<number | undefined> | null | undefined
): boolean => {
	if (!Array.isArray(arr)) {
		return true
	}
	return arr.every(value => value === 0 || value === undefined)
}

/**
 * Checks if all elements in the provided array are undefined.
 * Handles null, undefined, or non-array inputs by returning true, similar to an empty array.
 *
 * @param arr - An array of numbers or undefined values to check. Can be null or undefined.
 * @returns `true` if every element in the array is undefined, or if `arr` is null, undefined, not an array, or an empty array. Returns `false` otherwise.
 */
export const isAllUndefined = (
	arr: ReadonlyArray<number | undefined> | null | undefined
): boolean => {
	if (!Array.isArray(arr)) {
		return true
	}
	return arr.every(value => value === undefined)
}

export const dataArrayFormat = (
	data: MetricFrontendDataPoint[][],
	key: keyof MetricFrontendDataPoint,
	roundOffValue: boolean = false
): number[] => {
	const flattenedData = (data ?? []).flat()
	return flattenedData?.map(metric => {
		const value = metric[key] as number | undefined
		return roundOffValue ? Math.round(value ?? 0) : (value ?? 0)
	})
}

/**
 * Calculates and returns UTC-based date values for a given reporting period.
 *
 * This utility function is designed to avoid local timezone issues by using UTC methods.
 * It determines the start month, start year, and other relevant date values based on the provided `Period`.
 *
 * @param period - The reporting period for which to calculate date values. Supported values are:
 *   - `Period.FULL_YEAR`: Returns values for the last three full years, the current year, and the next year.
 *   - `Period.YTD`: Returns values from January of the current year to the current month.
 *   - `Period.LTM`: Returns values for the last twelve months ending with the current month.
 *   - Any other value defaults to YTD behavior.
 *
 * @returns An object containing:
 *   - `currentMonth`: The current month (0-based, UTC).
 *   - `currentYear`: The current year (UTC).
 *   - `startMonth`: The calculated start month (0-based, UTC).
 *   - `startYear`: The calculated start year (UTC).
 *   - `startFullYear`: An array of years for full-year periods, or `undefined` for others.
 *   - `compareCurrYear`: The current year (UTC), for comparison purposes.
 *   - `comparePriorYear`: The prior year (UTC), for comparison purposes.
 */
export const getUtcDateVals = (period: Period): UtcDateValues => {
	// replace current `getDateVals` function with this one to avoid UTC issues
	const currentDate = new Date()
	const currentYear = currentDate.getUTCFullYear()
	const currentMonth = currentDate.getUTCMonth()

	let startMonth: number
	let startYear: number
	let startFullYear: number[] | undefined

	switch (period) {
		case Period.FULL_YEAR:
			startMonth = 0 // January
			startYear = currentYear - 3
			startFullYear = [
				currentYear - 3,
				currentYear - 2,
				currentYear - 1,
				currentYear,
				currentYear + 1,
			]
			break
		case Period.YTD:
			startMonth = 0 // January
			startYear = currentYear
			startFullYear = undefined
			break
		case Period.LTM:
			startMonth = currentMonth
			startYear = currentYear - 1
			startFullYear = undefined
			break
		default:
			// Default to YTD behavior
			console.warn(
				`[PerformanceMetrics] Unhandled period type: ${period}, defaulting to YTD start values.`
			)
			startMonth = 0 // January
			startYear = currentYear
			startFullYear = undefined
			break
	}

	const compareCurrYear = currentYear
	const comparePriorYear = currentYear - 1

	return {
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		startFullYear,
		compareCurrYear,
		comparePriorYear,
	}
}

/**
 * Filters and sorts an array of metric data points based on the specified period and date range.
 *
 * If the period is `FULL_YEAR` and `startFullYear` is provided, it filters and sorts metrics for each year in `startFullYear`.
 * Otherwise, it filters metrics for the `currentYear` and sorts them.
 *
 * Before filtering, it pads the metrics with zero-value entries for missing months starting from `startMonth` and `startYear`.
 * All sorting is based on the `sequenceNo` property of the metrics.
 *
 * @param metrics - An array of metric data points to be filtered and sorted.
 * @param period - The time period to filter by (e.g., `Period.FULL_YEAR`, `Period.MONTHLY`).
 * @param currentMonth - The current month (0-indexed, e.g., 0 for January). Used when period is not `FULL_YEAR`.
 * @param currentYear - The current year. Used as the primary filter year if not `FULL_YEAR` period.
 * @param startMonth - The starting month (0-indexed) for padding missing months.
 * @param startYear - The starting year for padding missing months.
 * @param startFullYear - An optional array of years to filter by when `period` is `FULL_YEAR`.
 * @returns A 2D array of `MetricFrontendDataPoint`.
 *          If `period` is `FULL_YEAR` and `startFullYear` is provided, each inner array corresponds to a year in `startFullYear`.
 *          Otherwise, it returns an array containing a single inner array with metrics for the `currentYear`.
 */
export const filterSortItemByDate = (
	metrics: MetricFrontendDataPoint[],
	period: Period,
	currentMonth: number,
	currentYear: number,
	startMonth: number,
	startYear: number,
	startFullYear: number[] | undefined
): MetricFrontendDataPoint[][] => {
	metrics.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
	const paddedMetrics: MetricFrontendDataPoint[] = padMonthsWithZero(
		metrics,
		startMonth,
		startYear
	)

	if (
		period === Period.FULL_YEAR &&
		startFullYear &&
		startFullYear.length > 0
	) {
		const resultByYear: MetricFrontendDataPoint[][] = []
		for (const targetYear of startFullYear) {
			const yearSpecificMetrics = paddedMetrics
				.filter(metric => {
					const dateStr = metric.metricStartDt ?? ''
					const [year, month, day] = dateStr.split('-').map(Number)

					// Create a UTC date (note: month is 0-indexed)
					const metricDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))

					const metricYear = metricDate.getUTCFullYear()

					if (metricYear !== targetYear) {
						return false
					}
					return true
				})
				.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
			resultByYear.push(yearSpecificMetrics)
		}
		return resultByYear
	}
	const filteredSorted = paddedMetrics
		.filter(metric => {
			const metricDate = new Date((metric.metricStartDt ?? '') + 'T00:00:00')
			const metricYear = metricDate.getUTCFullYear()

			return metricYear === currentYear
		})
		.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
	return [filteredSorted]
}

export interface FilteredMetricsByYear {
	gciData: MetricFrontendDataPoint[][]
	sidesData: MetricFrontendDataPoint[][]
	salesVolumeData: MetricFrontendDataPoint[][]
}

/**
 * Filters and sorts GCI, sides, and sales volume metrics from the provided performance metrics API data
 * for the full year period, based on the current and start date values.
 *
 * @param graphData - The performance metrics API data containing metrics to be filtered and sorted.
 * @returns An object containing `gciData`, `sidesData`, and `salesVolumeData`,
 *          each as a two-dimensional array of metric frontend data points, filtered and sorted by year.
 */
export const filterSortByYear = (
	graphData: PerformanceMetricsApiData
): FilteredMetricsByYear => {
	const { currentMonth, currentYear, startMonth, startYear, startFullYear } =
		getUtcDateVals(Period.FULL_YEAR)

	const gciData = filterSortItemByDate(
		graphData.gciMetrics,
		Period.FULL_YEAR,
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		startFullYear
	)

	const sidesData = filterSortItemByDate(
		graphData.sidesMetrics,
		Period.FULL_YEAR,
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		startFullYear
	)

	const salesVolumeData = filterSortItemByDate(
		graphData.salesVolumeMetrics,
		Period.FULL_YEAR,
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		startFullYear
	)

	return { gciData, sidesData, salesVolumeData }
}

export const organizeByStatus = (
	data: MetricFrontendDataPoint[][],
	currentMonth: number,
	currentYear: number,
	startFullYear: number[] | undefined,
	roundOffValue: boolean = false,
	otherRevenueClosed: boolean = false
) => {
	const processYearData = (
		yearData: MetricFrontendDataPoint[],
		key: keyof MetricFrontendDataPoint
	): number[] => {
		return yearData.map(metric => {
			const value = metric[key] as number | undefined
			return roundOffValue ? Math.round(value ?? 0) : (value ?? 0)
		})
	}

	return {
		openValues: data.map(yearData =>
			processYearData(yearData, MetricKeysDataPoint.openNo)
		),
		closedValues: data.map(yearData =>
			processYearData(yearData, MetricKeysDataPoint.closedNo)
		),
		...(otherRevenueClosed && {
			otherRevenueClosedValues: data.map(yearData =>
				processYearData(yearData, MetricKeysDataPoint.otherRevenueClosedNo)
			),
		}),

		pendingValues: data.map(yearData =>
			processYearData(yearData, MetricKeysDataPoint.pendingNo)
		),
		compareCurrentClosedNo: data.map(yearData =>
			processYearData(yearData, MetricKeysDataPoint.closedNo)
		),
		compareCurrentOpenNo: data.map(yearData =>
			processYearData(yearData, MetricKeysDataPoint.openNo)
		),
	}
}
